// Wall Mode runtime (React port). Paged panels, runs MQTT action sequences,
// keeps the screen awake (Screen Wake Lock API), tap-to-wake black overlay, and
// a PIN-guarded exit. Landscape is requested where the browser allows it.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Button, CircularProgress } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useApp } from '../../../../core/state/AppContext';
import { fetchPanels, runSequence } from './wallApi';

export default function WallModeScreen() {
  const { userId, user, colors } = useApp();
  const navigate = useNavigate();
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [screenOff, setScreenOff] = useState(false);
  const wakeRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPanels(await fetchPanels(userId));
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Keep screen awake.
  useEffect(() => {
    let released = false;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) wakeRef.current = await navigator.wakeLock.request('screen');
      } catch (e) { /* not supported / denied */ }
    };
    acquire();
    const onVis = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVis);
    // Best-effort landscape lock (supported on some Android browsers in fullscreen).
    try { window.screen?.orientation?.lock?.('landscape').catch(() => {}); } catch (e) { /* ignore */ }
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVis);
      try { wakeRef.current?.release?.(); } catch (e) { /* ignore */ }
      try { window.screen?.orientation?.unlock?.(); } catch (e) { /* ignore */ }
      if (released) wakeRef.current = null;
    };
  }, []);

  const goToPanelId = (panelId) => {
    const idx = panels.findIndex((p) => p.WallPanelId === panelId);
    if (idx >= 0) setPage(idx);
  };

  const onButton = (b) => {
    if (b.ButtonType === 'navigate') goToPanelId(b.TargetWallPanelId);
    else runSequence(userId, b);
  };

  const exitWithPin = () => {
    const entered = window.prompt('Enter PIN to exit Wall Mode');
    if (entered == null) return;
    if (entered === String(user?.Password)) navigate('/home');
    else window.alert('Wrong PIN');
  };

  if (loading) {
    return <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}><CircularProgress /></Box>;
  }

  if (panels.length === 0) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: colors.back }}>
        <Typography sx={{ color: colors.text }} variant="h5">No wall panels yet</Typography>
        <Button variant="contained" onClick={() => navigate('/wall/design')}>Design a panel</Button>
        <Button onClick={exitWithPin} sx={{ color: colors.text }}>Exit</Button>
      </Box>
    );
  }

  const panel = panels[page];
  const cols = Math.max(1, panel.ColumnsCount);

  return (
    <Box sx={{ height: '100vh', bgcolor: panel.BackColor || colors.back, position: 'relative', overflow: 'hidden' }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
        <Typography sx={{ flex: 1, color: colors.text, fontWeight: 600, pl: 1 }}>{panel.PanelName}</Typography>
        <IconButton onClick={() => setScreenOff(true)} sx={{ color: colors.text }}><PowerSettingsNewIcon /></IconButton>
        <IconButton onClick={async () => { navigate('/wall/design'); }} sx={{ color: colors.text }}><DashboardCustomizeIcon /></IconButton>
        <IconButton onClick={exitWithPin} sx={{ color: colors.text }}><CloseIcon /></IconButton>
      </Box>

      {/* Button grid */}
      <Box sx={{
        display: 'grid', gap: 1.5, p: 1.5,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridAutoRows: '90px',
        height: 'calc(100vh - 110px)', overflowY: 'auto',
      }}>
        {panel.Buttons.map((b) => (
          <Box
            key={b.WallButtonId}
            onClick={() => onButton(b)}
            sx={{
              gridColumn: `span ${Math.min(b.Width, cols)}`,
              gridRow: `span ${b.Height}`,
              bgcolor: b.ButtonColor || '#37474F',
              color: b.TextColor || '#fff',
              borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', p: 1, cursor: 'pointer', userSelect: 'none', fontWeight: 600,
            }}
          >
            {b.ButtonType === 'navigate' ? `â–¶ ${b.ButtonText}` : b.ButtonText}
          </Box>
        ))}
      </Box>

      {/* Pager */}
      <Box sx={{ position: 'absolute', bottom: 6, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} sx={{ color: colors.text }}><ChevronLeftIcon /></IconButton>
        {panels.map((_, i) => (
          <Box key={i} sx={{ width: i === page ? 18 : 6, height: 6, borderRadius: 3, bgcolor: i === page ? colors.button : 'rgba(255,255,255,0.4)' }} />
        ))}
        <IconButton size="small" disabled={page === panels.length - 1} onClick={() => setPage((p) => p + 1)} sx={{ color: colors.text }}><ChevronRightIcon /></IconButton>
      </Box>

      {/* Tap-to-wake overlay */}
      {screenOff && (
        <Box onClick={() => setScreenOff(false)} sx={{ position: 'absolute', inset: 0, bgcolor: '#000', zIndex: 10 }} />
      )}
    </Box>
  );
}
